/**
 * CustomNodeBuilder Component
 * UI for creating and editing custom node definitions
 */

import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import type { CustomNodeDefinition, ConnectorConfig } from '../types/customNode';
import type { ConnectorType } from '../types/nodeConfig';
import CustomNodeStorage from '../services/CustomNodeStorage';
import { validateTemplate, extractVariables, generateVariableName } from '../utils/promptTemplateUtils';
import { migrateSingleCustomNode } from '../utils/customNodeMigration';
import { alertService } from './Alert';
import logger from '../utils/logger';
import './CustomNodeBuilder.css';

// Material UI Icons
import BuildIcon from '@mui/icons-material/Build';
import SettingsIcon from '@mui/icons-material/Settings';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import PaletteIcon from '@mui/icons-material/Palette';
import DescriptionIcon from '@mui/icons-material/Description';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import GpsFixedIcon from '@mui/icons-material/GpsFixed';
import BarChartIcon from '@mui/icons-material/BarChart';
import SearchIcon from '@mui/icons-material/Search';
import StarIcon from '@mui/icons-material/Star';
import BoltIcon from '@mui/icons-material/Bolt';
import TheaterComedyIcon from '@mui/icons-material/TheaterComedy';
import MovieIcon from '@mui/icons-material/Movie';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import ImageIcon from '@mui/icons-material/Image';
import VideocamIcon from '@mui/icons-material/Videocam';
import PsychologyIcon from '@mui/icons-material/Psychology';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import CodeIcon from '@mui/icons-material/Code';

// Icon mapping for selection and rendering
const ICON_OPTIONS = {
  'Build': BuildIcon,
  'Settings': SettingsIcon,
  'AutoAwesome': AutoAwesomeIcon,
  'Palette': PaletteIcon,
  'Description': DescriptionIcon,
  'Lightbulb': LightbulbIcon,
  'RocketLaunch': RocketLaunchIcon,
  'GpsFixed': GpsFixedIcon,
  'BarChart': BarChartIcon,
  'Search': SearchIcon,
  'Star': StarIcon,
  'Bolt': BoltIcon,
  'TheaterComedy': TheaterComedyIcon,
  'Movie': MovieIcon,
  'CameraAlt': CameraAltIcon,
  'Image': ImageIcon,
  'Videocam': VideocamIcon,
  'Psychology': PsychologyIcon,
  'SmartToy': SmartToyIcon,
  'Code': CodeIcon
};

/**
 * Calculate contrast color (black or white) for given background color
 */
function getContrastColor(hexColor: string): string {
  // Convert hex to RGB
  const hex = hexColor.replace('#', '');
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  // Calculate relative luminance (WCAG formula)
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

  // Return black for light backgrounds, white for dark backgrounds
  return luminance > 0.5 ? '#000000' : '#FFFFFF';
}

interface CustomNodeBuilderProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: () => void;
  editingNode?: CustomNodeDefinition;
}

const CustomNodeBuilder: React.FC<CustomNodeBuilderProps> = ({
  isOpen,
  onClose,
  onSave,
  editingNode
}) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [icon, setIcon] = useState('Build');
  const [color, setColor] = useState('#6366F1');
  const [category] = useState<'prompt' | 'tool'>('prompt');
  const [inputs, setInputs] = useState<ConnectorConfig[]>([]);
  const [outputs, setOutputs] = useState<ConnectorConfig[]>([{ id: 'output-1', type: 'text', label: 'Output', position: 'middle' }]);
  const [promptTemplate, setPromptTemplate] = useState('');
  const [hasAdditionalInstructions, setHasAdditionalInstructions] = useState(false);
  const [additionalInstructionsLabel, setAdditionalInstructionsLabel] = useState('Additional Instructions');
  const [aiModel, setAiModel] = useState<CustomNodeDefinition['aiModel']>('gpt-4o-mini');
  const [aspectRatio, setAspectRatio] = useState('9:16');
  const [durationSeconds, setDurationSeconds] = useState('8');

  // Reset/load form when modal opens or editingNode changes
  React.useEffect(() => {
    if (isOpen) {
      if (editingNode) {
        // Load editing node data
        setName(editingNode.name);
        setDescription(editingNode.description || '');
        setIcon(editingNode.icon);
        setColor(editingNode.color);
        setInputs(editingNode.inputs);
        setOutputs(editingNode.outputs);
        setPromptTemplate(editingNode.promptTemplate);
        setHasAdditionalInstructions(editingNode.hasAdditionalInstructions);
        setAdditionalInstructionsLabel(editingNode.additionalInstructionsLabel || 'Additional Instructions');
        setAiModel(editingNode.aiModel);
        setAspectRatio(editingNode.aspectRatio || '9:16');
        setDurationSeconds(editingNode.durationSeconds || '8');
      } else {
        // Reset to defaults for new node
        setName('');
        setDescription('');
        setIcon('Build');
        setColor('#6366F1');
        setInputs([]);
        setOutputs([{ id: 'output-1', type: 'text', label: 'Output', position: 'middle' }]);
        setPromptTemplate('');
        setHasAdditionalInstructions(false);
        setAdditionalInstructionsLabel('Additional Instructions');
        setAiModel('gpt-4o-mini');
        setAspectRatio('9:16');
        setDurationSeconds('8');
      }
    }
  }, [isOpen, editingNode]);

  // Determine if model generates images or videos
  const isImageModel = aiModel === 'nano-banana';
  const isVideoModel = aiModel === 'veo-3.1-fast' || aiModel === 'veo-3.0-fast';

  const handleAddInput = () => {
    const newId = `input-${inputs.length + 1}`;
    const newLabel = `Input ${inputs.length + 1}`;
    setInputs([
      ...inputs,
      {
        id: newId,
        type: 'text',
        label: newLabel,
        position: 'middle',
        variableName: generateVariableName(newLabel)
      }
    ]);
  };

  const handleRemoveInput = (index: number) => {
    setInputs(inputs.filter((_, i) => i !== index));
  };

  const handleUpdateInput = (index: number, field: keyof ConnectorConfig, value: any) => {
    const updated = [...inputs];
    updated[index] = { ...updated[index], [field]: value };

    // Auto-generate variable name when label changes
    if (field === 'label') {
      updated[index].variableName = generateVariableName(value);
    }

    setInputs(updated);
  };

  const handleAddOutput = () => {
    setOutputs([
      ...outputs,
      {
        id: `output-${outputs.length + 1}`,
        type: 'text',
        label: `Output ${outputs.length + 1}`,
        position: 'middle'
      }
    ]);
  };

  const handleRemoveOutput = (index: number) => {
    if (outputs.length > 1) {
      setOutputs(outputs.filter((_, i) => i !== index));
    } else {
      alertService.error('At least one output is required');
    }
  };

  const handleUpdateOutput = (index: number, field: keyof ConnectorConfig, value: any) => {
    const updated = [...outputs];
    updated[index] = { ...updated[index], [field]: value };
    setOutputs(updated);
  };

  const handleSave = () => {
    // Validation
    if (!name.trim()) {
      alertService.error('Node name is required');
      return;
    }

    if (!promptTemplate.trim()) {
      alertService.error('Prompt template is required');
      return;
    }

    // Validate template
    const validation = validateTemplate(promptTemplate);
    if (!validation.valid) {
      alertService.error('Template validation failed: ' + validation.errors.join(', '));
      return;
    }

    // Check for duplicate name
    if (CustomNodeStorage.existsByName(name, editingNode?.id)) {
      alertService.error('A custom node with this name already exists');
      return;
    }

    // Create definition
    const definition: CustomNodeDefinition = {
      id: editingNode?.id || `custom-${Date.now()}`,
      name: name.trim(),
      description: description.trim() || undefined,
      icon,
      color,
      category,
      inputs,
      outputs,
      promptTemplate: promptTemplate.trim(),
      hasAdditionalInstructions,
      additionalInstructionsLabel: additionalInstructionsLabel.trim(),
      aiModel,
      aspectRatio: isImageModel || isVideoModel ? aspectRatio : undefined,
      durationSeconds: isVideoModel ? durationSeconds : undefined,
      createdAt: editingNode?.createdAt || new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };

    try {
      // Migrate to UniversalNode format automatically
      const migratedDefinition = migrateSingleCustomNode(definition);
      
      // Save the migrated definition
      CustomNodeStorage.save(migratedDefinition);
      
      logger.info('[CustomNodeBuilder] Saved custom node (migrated to UniversalNode):', definition.name);
      alertService.success(`Custom node "${definition.name}" saved successfully`);
      onSave();
      onClose();
    } catch (error) {
      logger.error('[CustomNodeBuilder] Failed to save:', error);
      alertService.error('Failed to save custom node');
    }
  };

  const handleDelete = () => {
    if (!editingNode) return;

    if (window.confirm(`Are you sure you want to delete "${editingNode.name}"?\n\nThis cannot be undone.`)) {
      try {
        CustomNodeStorage.delete(editingNode.id);
        logger.info('[CustomNodeBuilder] Deleted custom node:', editingNode.name);
        alertService.success(`Custom node "${editingNode.name}" deleted successfully`);
        onSave(); // Trigger refresh of custom node list
        onClose();
      } catch (error) {
        logger.error('[CustomNodeBuilder] Failed to delete:', error);
        alertService.error('Failed to delete custom node');
      }
    }
  };

  if (!isOpen) return null;

  const templateVariables = extractVariables(promptTemplate);

  return createPortal(
    <div className="custom-node-builder-overlay" onClick={onClose}>
      <div className="custom-node-builder-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{editingNode ? 'Edit Custom Node' : 'Create Custom Node'}</h2>
          <button className="close-button" onClick={onClose}>✕</button>
        </div>

        <div className="modal-body">
          {/* Basic Info */}
          <section className="builder-section">
            <h3>Basic Information</h3>
            <div className="form-row">
              <label>
                Node Name:
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g., SEO Analyzer"
                  className="nodrag"
                />
              </label>
            </div>

            <div className="form-row">
              <label>
                Description (optional):
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Explain what this node does..."
                  className="nodrag"
                  rows={2}
                  style={{ resize: 'vertical' }}
                />
              </label>
            </div>

            <div className="form-row-inline">
              <label>
                Icon:
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <div style={{ 
                    width: '60px', 
                    height: '36px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: '1px solid var(--node-border-color)',
                    borderRadius: '6px',
                    background: 'var(--color-bg-primary)'
                  }}>
                    {React.createElement(ICON_OPTIONS[icon as keyof typeof ICON_OPTIONS] || BuildIcon, { 
                      sx: { fontSize: '20px' } 
                    })}
                  </div>
                  <select
                    value={icon}
                    onChange={(e) => setIcon(e.target.value)}
                    className="nodrag"
                    style={{ flex: 1 }}
                  >
                    <option value="Build">🔧 Build / Tool</option>
                    <option value="Settings">⚙️ Settings</option>
                    <option value="AutoAwesome">✨ Auto Awesome / Sparkles</option>
                    <option value="Palette">🎨 Palette / Art</option>
                    <option value="Description">📝 Document</option>
                    <option value="Lightbulb">💡 Lightbulb / Idea</option>
                    <option value="RocketLaunch">🚀 Rocket Launch</option>
                    <option value="GpsFixed">🎯 Target / GPS</option>
                    <option value="BarChart">📊 Bar Chart</option>
                    <option value="Search">🔍 Search</option>
                    <option value="Star">⭐ Star</option>
                    <option value="Bolt">⚡ Bolt / Lightning</option>
                    <option value="TheaterComedy">🎭 Theater</option>
                    <option value="Movie">Movie</option>
                    <option value="CameraAlt">Camera</option>
                    <option value="Image">Image / Frame</option>
                    <option value="Videocam">Video Camera</option>
                    <option value="Psychology">Brain / Psychology</option>
                    <option value="SmartToy">Robot / AI</option>
                    <option value="Code">Code</option>
                  </select>
                </div>
              </label>
              <label>
                Color:
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <input
                    type="color"
                    value={color}
                    onChange={(e) => setColor(e.target.value)}
                    className="nodrag"
                    style={{ width: '60px', height: '36px', cursor: 'pointer' }}
                  />
                  <div
                    style={{
                      flex: 1,
                      height: '36px',
                      borderRadius: '4px',
                      background: color,
                      border: '1px solid var(--node-border-color)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '12px',
                      fontWeight: 500,
                      color: getContrastColor(color)
                    }}
                  >
                    {color}
                  </div>
                </div>
              </label>
              <label>
                AI Model:
                <select
                  value={aiModel}
                  onChange={(e) => setAiModel(e.target.value as any)}
                  className="nodrag"
                >
                  <optgroup label="Text Generation">
                    <option value="gpt-4o-mini">GPT-4o Mini (OpenAI)</option>
                    <option value="gemini-2.5-flash">Gemini 2.5 Flash (Google)</option>
                  </optgroup>
                  <optgroup label="Image Generation">
                    <option value="nano-banana">Nano Banana 🍌 / Gemini 2.5 Flash Image (Google)</option>
                  </optgroup>
                  <optgroup label="Video Generation">
                    <option value="veo-3.1-fast">VEO 3.1 Fast (Google)</option>
                    <option value="veo-3.0-fast">VEO 3.0 Fast (Google)</option>
                  </optgroup>
                </select>
              </label>
            </div>

            {/* Conditional parameters for image/video models */}
            {(isImageModel || isVideoModel) && (
              <div className="form-row-inline" style={{ marginTop: '12px' }}>
                <label>
                  Aspect Ratio:
                  <select
                    value={aspectRatio}
                    onChange={(e) => setAspectRatio(e.target.value)}
                    className="nodrag"
                  >
                    <option value="9:16">9:16 (Portrait)</option>
                    <option value="1:1">1:1 (Square)</option>
                    <option value="4:5">4:5</option>
                    <option value="16:9">16:9 (Landscape)</option>
                    <option value="4:3">4:3</option>
                    <option value="3:4">3:4</option>
                    <option value="3:2">3:2</option>
                    <option value="2:3">2:3</option>
                    <option value="5:4">5:4</option>
                  </select>
                </label>
                {isVideoModel && (
                  <label>
                    Duration:
                    <select
                      value={durationSeconds}
                      onChange={(e) => setDurationSeconds(e.target.value)}
                      className="nodrag"
                    >
                      <option value="4">4 seconds</option>
                      <option value="6">6 seconds</option>
                      <option value="8">8 seconds</option>
                    </select>
                  </label>
                )}
              </div>
            )}
          </section>

          {/* Inputs */}
          <section className="builder-section">
            <h3>Inputs</h3>
            {inputs.map((input, idx) => (
              <div key={input.id} className="connector-row">
                <input
                  type="text"
                  value={input.label}
                  onChange={(e) => handleUpdateInput(idx, 'label', e.target.value)}
                  placeholder="Label"
                  className="nodrag"
                />
                <select
                  value={input.type}
                  onChange={(e) => handleUpdateInput(idx, 'type', e.target.value as ConnectorType)}
                  className="nodrag"
                >
                  <option value="text">Text</option>
                  <option value="image">Image</option>
                  <option value="video">Video</option>
                </select>
                <input
                  type="text"
                  value={input.variableName || ''}
                  onChange={(e) => handleUpdateInput(idx, 'variableName', e.target.value)}
                  placeholder="Variable name"
                  className="nodrag variable-input"
                  title="Use this in template as {{variableName}}"
                />
                <button onClick={() => handleRemoveInput(idx)} className="remove-button">✕</button>
              </div>
            ))}
            <button onClick={handleAddInput} className="add-button">+ Add Input</button>
          </section>

          {/* Outputs */}
          <section className="builder-section">
            <h3>Outputs</h3>
            {outputs.map((output, idx) => (
              <div key={output.id} className="connector-row">
                <input
                  type="text"
                  value={output.label}
                  onChange={(e) => handleUpdateOutput(idx, 'label', e.target.value)}
                  placeholder="Label"
                  className="nodrag"
                />
                <select
                  value={output.type}
                  onChange={(e) => handleUpdateOutput(idx, 'type', e.target.value as ConnectorType)}
                  className="nodrag"
                >
                  <option value="text">Text</option>
                  <option value="image">Image</option>
                  <option value="video">Video</option>
                </select>
                <button
                  onClick={() => handleRemoveOutput(idx)}
                  className="remove-button"
                  disabled={outputs.length === 1}
                >✕</button>
              </div>
            ))}
            <button onClick={handleAddOutput} className="add-button">+ Add Output</button>
          </section>

          {/* Prompt Template */}
          <section className="builder-section">
            <h3>Prompt Template</h3>
            {templateVariables.length > 0 && (
              <div className="variables-hint">
                Available variables: {templateVariables.map(v => `{{${v}}}`).join(', ')}
              </div>
            )}
            <textarea
              value={promptTemplate}
              onChange={(e) => setPromptTemplate(e.target.value)}
              placeholder="Enter your prompt template here...&#10;&#10;Use {{variableName}} to insert input values."
              className="nodrag prompt-template-textarea"
              rows={10}
            />
          </section>

          {/* Additional Instructions */}
          <section className="builder-section">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={hasAdditionalInstructions}
                onChange={(e) => setHasAdditionalInstructions(e.target.checked)}
                className="nodrag"
              />
              Include "Additional Instructions" field in node
            </label>
            {hasAdditionalInstructions && (
              <input
                type="text"
                value={additionalInstructionsLabel}
                onChange={(e) => setAdditionalInstructionsLabel(e.target.value)}
                placeholder="Field label"
                className="nodrag"
                style={{ marginTop: '8px' }}
              />
            )}
          </section>
        </div>

        <div className="modal-footer">
          {editingNode && (
            <button onClick={handleDelete} className="delete-button" style={{ marginRight: 'auto' }}>
              Delete Node
            </button>
          )}
          <button onClick={onClose} className="cancel-button">Cancel</button>
          <button onClick={handleSave} className="save-button">
            {editingNode ? 'Update' : 'Create'} Node
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default CustomNodeBuilder;
